import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { useMediaUrl } from './media.js';

// Foto de perfil de verdade: fica no perfil da pessoa no mensageiro e aparece
// para quem conversa com ela. Sem foto, o personagem da casa continua no lugar.
const PHOTO_EVENT = 'secretario:photo';
const TTL_MS = 5 * 60 * 1000;
const PHOTO_EDGE = 512;
const known = new Map<string, { at: number; mxc: Promise<string | null> }>();

function remember(userId: string, mxc: string | null): void {
  known.set(userId, { at: Date.now(), mxc: Promise.resolve(mxc) });
  window.dispatchEvent(new CustomEvent(PHOTO_EVENT, { detail: { userId } }));
}

/** Recorta o centro em quadrado e reduz: o que sobe é leve e sem dados da câmera. */
export async function squarePhoto(file: Blob, edge = PHOTO_EDGE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = edge;
    canvas.height = edge;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Não consegui preparar a foto.');
    context.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      edge,
      edge,
    );
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.88),
    );
    if (!blob) throw new Error('Não consegui preparar a foto.');
    return blob;
  } finally {
    bitmap.close();
  }
}

export async function setMyPhoto(client: MatrixClient, photo: Blob): Promise<void> {
  const { content_uri: mxc } = await client.uploadContent(photo, {
    type: 'image/jpeg',
    includeFilename: false,
  });
  await client.setAvatarUrl(mxc);
  remember(client.getSafeUserId(), mxc);
}

export async function removeMyPhoto(client: MatrixClient): Promise<void> {
  await client.setAvatarUrl('');
  remember(client.getSafeUserId(), null);
}

/** Endereço da foto de alguém (ou nada). Guardado por alguns minutos. */
export function personPhotoMxc(client: MatrixClient, userId: string): Promise<string | null> {
  const hit = known.get(userId);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.mxc;
  const mxc = client
    .getProfileInfo(userId, 'avatar_url')
    .then((info) => info.avatar_url || null)
    .catch(() => null);
  known.set(userId, { at: Date.now(), mxc });
  return mxc;
}

export function usePersonPhotoMxc(
  client: MatrixClient | null,
  userId: string | null,
): string | null {
  const [state, setState] = useState<{ userId: string | null; mxc: string | null }>({
    userId: null,
    mxc: null,
  });
  useEffect(() => {
    if (!client || !userId) return;
    let cancelled = false;
    const load = () =>
      void personPhotoMxc(client, userId).then((mxc) => !cancelled && setState({ userId, mxc }));
    load();
    const onChange = (event: Event) => {
      if ((event as CustomEvent<{ userId: string }>).detail?.userId === userId) load();
    };
    window.addEventListener(PHOTO_EVENT, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(PHOTO_EVENT, onChange);
    };
  }, [client, userId]);
  return state.userId === userId ? state.mxc : null;
}

/** Foto pronta para exibir (endereço local) ou nada. */
export function usePersonPhoto(client: MatrixClient | null, userId: string | null): string | null {
  const mxc = usePersonPhotoMxc(client, userId);
  return useMediaUrl(client, mxc ? { url: mxc, mime: 'image/jpeg' } : null).url;
}

export function useMyPhoto(client: MatrixClient | null): string | null {
  return usePersonPhoto(client, client?.getUserId() ?? null);
}

export function userIdOf(client: MatrixClient, username: string): string {
  return `@${username}:${client.getDomain()}`;
}
